import { ShieldBan, Workflow } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';

import { UserAvatarGroup } from '@/components/user-avatar';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/utils/format-date';

/**
 * `row` keeps the icon beside the name, for the chat and the agent rail.
 * `stacked` gives the icon its own line above the name, for the workflows grid.
 */
type WorkflowCardLayout = 'row' | 'stacked';

export interface WorkflowCardProps {
  name: string;
  description?: string | null;
  isPublic?: boolean;
  nodeCount?: number;
  lastRunAt?: string | null;
  clientData?: boolean;
  collaborators?: string[];
  overflowMenu?: ReactNode;
  onOpen?: () => void;
  layout?: WorkflowCardLayout;
  className?: string;
  testId?: string;
}

export function WorkflowCard({
  name,
  description,
  isPublic = false,
  nodeCount,
  lastRunAt,
  clientData = false,
  collaborators = [],
  overflowMenu,
  onOpen,
  layout = 'row',
  className,
  testId,
}: WorkflowCardProps) {
  const meta: string[] = [
    isPublic ? 'Public' : 'Private',
    ...(typeof nodeCount === 'number'
      ? [`${nodeCount} ${nodeCount === 1 ? 'Node' : 'Nodes'}`]
      : []),
    ...(lastRunAt ? [`Last run ${formatTimeAgo(lastRunAt)}`] : []),
  ];

  const shell = {
    'data-testid': testId,
    'data-layout': layout,
    role: onOpen ? 'link' : undefined,
    tabIndex: onOpen ? 0 : undefined,
    onClick: onOpen,
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onOpen();
      }
    },
  };

  const shellClassName = cn(
    'group w-full overflow-hidden rounded-xl bg-accent p-5 text-left shadow-sm transition-shadow',
    onOpen && 'cursor-pointer hover:shadow-md',
  );

  const icon = (
    <div className='flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-background shadow-sm'>
      <Workflow className='size-6' />
    </div>
  );

  const controls = (
    <div className='flex shrink-0 items-center gap-2'>
      {clientData && (
        <span className='flex items-center gap-1 rounded bg-black/80 px-2 py-1 text-xs font-medium text-yellow-500'>
          <ShieldBan className='size-3.5' />
          Client Data
        </span>
      )}
      {overflowMenu}
    </div>
  );

  const text = (
    <div className='min-w-0'>
      <p className='truncate text-base font-medium text-foreground'>{name}</p>
      {description && (
        <p className='mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground'>
          {description}
        </p>
      )}
    </div>
  );

  const metaLine = (
    <div
      data-testid='workflow-card-meta'
      className={cn(
        'flex min-h-6 min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground',
        layout === 'row' && 'col-start-2',
      )}
    >
      {meta.map((item, index) => (
        <span key={item} className='contents'>
          {index > 0 && (
            <span className='size-0.5 shrink-0 rounded-full bg-muted-foreground' />
          )}
          <span className='whitespace-nowrap'>{item}</span>
        </span>
      ))}
    </div>
  );

  const collaboratorStack = (
    // The separator between overlapping faces is a ring rather than a border
    // so it sits outside the circle instead of eating into it.
    <UserAvatarGroup
      emails={collaborators}
      className={layout === 'row' ? 'col-start-3 justify-self-end' : 'ml-auto'}
      avatarClassName='ring-2 ring-accent'
    />
  );

  if (layout === 'stacked') {
    return (
      <div
        {...shell}
        className={cn(shellClassName, 'flex flex-col gap-4', className)}
      >
        <div className='flex items-center justify-between gap-3'>
          {icon}
          {controls}
        </div>
        {text}
        <div className='flex items-center gap-4'>
          {metaLine}
          {collaboratorStack}
        </div>
      </div>
    );
  }

  return (
    // Three columns — icon, text, and the trailing controls — so the meta line
    // starts under the name while the collaborators land under the menu. A
    // card with no description collapses to two tight lines.
    <div
      {...shell}
      className={cn(
        shellClassName,
        'grid grid-cols-[3rem_minmax(0,1fr)_auto] items-start gap-x-3',
        description ? 'gap-y-4' : 'gap-y-1',
        className,
      )}
    >
      <div className='row-span-2'>{icon}</div>
      {text}
      {controls}
      {metaLine}
      {collaboratorStack}
    </div>
  );
}
